import React, { useState, useEffect, useRef } from 'react';
import {
    LinearProgress,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    Toolbar,
    Typography,
    Chip,
    ChipProps,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Tabs,
    Tab,
    Link as MuiLink,
    List,
    ListItem as ListItemBase,
    ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExtendedField from '../../components/text_field';
import SelectField from '../../components/select_field';
import Config from '../../config';
import EnhancedTable from '../../components/table';
import ConfirmDialog from '../../components/confirm_dialog';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Moment from 'react-moment';
import Helper, {validateEmail, readFileAsync} from '../../helpers';
import DownloadButton from '../../components/download_button';
import { Form, Field } from 'react-final-form';
import isEqual from 'lodash.isequal';
import Papa from 'papaparse';
import FileSaverBase from 'filesaver.js-npm';
import DropZone from '../files/dropzone';
import SearchBox from '../../components/searchbox';
import { withRouter } from "react-router";
import { CheckBox } from '@mui/icons-material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import momentjs from 'moment';
// Shared with the onboarding-intelligence module and onboarding_v2 — this is
// the canonical shape for a row from /api/odata/integrations.
import { OnboardingRow } from './onboardingIntelligence.types';

// Relax legacy `button` prop typing on ListItem to `any` (behavior unchanged).
const ListItem: any = ListItemBase;

// filesaver.js-npm ships a default export typed as `() => void`; cast to any
// to access the real runtime `.saveAs` method (same pattern used elsewhere,
// e.g. catalog_import.tsx, catalog_export.tsx, product.tsx).
const FileSaver: any = FileSaverBase;

interface PriorityChipProps extends Omit<ChipProps, 'value'> {
    value?: string;
}

// PureComponent -> React.memo preserves the shallow-props bail-out.
const PriorityChip = React.memo(({value, ...otherProps}: PriorityChipProps) => {
    let color = "#FFA500";
    if(value === "Low"){
        color = "#275E7A";
    }
    if(value === "High"){
        color = "#EE3224";
    }
    if(value === "Ignored"){
        color = "#555";
    }
    return <Chip label={value} style={{backgroundColor: color, color: 'white', fontWeight: 'bold', minWidth: '8em' }} {...otherProps}/>;
});

const StyledStatusChip = styled(Chip)({
    "& .MuiChip-label": {
        paddingRight: 0,
    },
    "& .errorLabel": {
        backgroundColor: "#ee3224",
        lineHeight: "32px",
        height: "32px",
        verticalAlign: "middle",
        display: "inline-block",
        padding: "2px",
        marginLeft: "5px",
        "& svg": {
            verticalAlign: "middle"
        }
    }
});

interface BareStatusChipProps extends Omit<ChipProps, 'value' | 'error'> {
    value?: string;
    error?: string;
}

// PureComponent -> React.memo preserves the shallow-props bail-out.
const BareStatusChip = React.memo(({value, error, ...otherProps}: BareStatusChipProps) => {
    let color = "#FFA500";
    if(value === "New" || value === "Onboarding" || value === "Deployed to Stage" || value === "Form Open" || value === "Invite Sent"){
        color = "#275E7A";
    }
    if(value === "Live"){
        color = "#448044";
    }
    return <StyledStatusChip label={error ? <>{value}<span className='errorLabel'><ErrorIcon/></span></> : <span style={{paddingRight: '12px'}}>{value}</span>}
    style={{backgroundColor: color, color: 'white', fontWeight: 'bold', minWidth: '8em', overflow: "hidden" }} {...otherProps}/>;
});

const StatusChip = BareStatusChip;

interface CreatedChipProps {
    value?: boolean;
}

// PureComponent -> React.memo preserves the shallow-props bail-out.
const CreatedChip = React.memo(({value}: CreatedChipProps) => {
    let color = "#EE3224";
    if(value){
        color = "#448044";
    }
    return <Chip label={value ? 'Success' : 'Error'} style={{backgroundColor: color, color: 'white', fontWeight: 'bold', minWidth: '8em' }}/>;
});

const OnboardingStatus = {
    InviteSent: "Invite Sent",
    FormReceived: "Form Received",
    Onboarding: "Onboarding",
    RequestedGoLive: "Go Live Requested"
};

interface RenderTagsProps {
    value?: string;
    addFilter: (filter: { operator: string; value: string; field: string }) => void;
}

const RenderTags = ({value, addFilter}: RenderTagsProps) => {
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const [showAll, setShowAll] = React.useState(false);
    const sizeRef = React.useRef<any>(null);
    React.useEffect(() => {
      const el = sizeRef.current;
      let overflow = el.offsetHeight < el.scrollHeight;
      if(overflow !== isOverflowing){
        setIsOverflowing(overflow);
      }
    }, [isOverflowing]);

    return (<>
        <div ref={sizeRef} style={{display: 'inline-flex', gap: '0.5em', justifyContent: 'center', flexWrap: 'wrap', maxHeight: showAll ? null : '2.2rem', overflow: 'hidden', padding: '0.1rem'}}>
            {(value || "").split("|").filter(r => !(!r)).map(r => <Chip onClick={() => addFilter({operator: 'contains', value: r, field: 'Tags'})} label={r} key={r}/>)}
        </div>
        {isOverflowing ? <Chip className='ellipsis' style={{display: 'inline-flex', position: 'absolute', marginLeft: '-15px', marginTop: '2px'}} onClick={() => {setShowAll(true); setIsOverflowing(false);}} label='...'/> : ''}
    </>);
  }

interface OnboardingProps {
    // Injected by withRouter() below.
    history?: any;
    location?: any;
    setTitle?: (title: string) => void;
}

// Loosely typed to match the class's actual dynamic usage (customFields,
// refresh, error, created, etc. are all added after the initial setState
// calls below).
interface OnboardingState {
    newIntegration: boolean;
    showConfirm: boolean;
    showError: boolean;
    term: string;
    termValue: string;
    selectedTab: string;
    tags: string[];
    showBulkActions: boolean;
    selected: OnboardingRow[];
    bulkResults: OnboardingRow[];
    testCaseGroups: any;
    loaded?: boolean;
    customFields?: any[];
    refresh?: boolean;
    error?: string;
    confirmMessage?: string;
    onConfirm?: () => void;
    importFile?: File | null;
    created?: any[];
    showIgnored?: boolean;
    bulkProgress?: number;
    [key: string]: unknown;
}

const Onboarding = (props: OnboardingProps) => {
    const [state, setState] = useState<OnboardingState>({ newIntegration: false, showConfirm: false, showError: false, term: '', termValue: '', selectedTab: 'single', tags: [], showBulkActions: false, selected: [], bulkResults: [], testCaseGroups: {} });
    // Mirrors `this.setState`: accepts a partial (merged into prev) or an
    // updater function, always merging into previous state so partial-merge
    // semantics are preserved.
    const mergeState = (patch: Partial<OnboardingState> | ((prev: OnboardingState) => Partial<OnboardingState>)) =>
        setState(prev => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
    // Referenced by the hidden agreement-upload <input> in render(), but
    // `saveAgreement` was never defined in the original component — calling it
    // would throw at runtime (dead code; the input is unreachable since nothing
    // clicks it). Typed as `any` here purely so the property access compiles,
    // preserving the original (broken-if-ever-hit) behavior.
    const agreementUpload = useRef<any>(null);
    const saveAgreement: any = undefined;
    useEffect(() => {
        const init = async () => {
            if(props.setTitle) {
                props.setTitle('Onboarding Partners');
            }
            let urlTerm = new Helper().getTermFromUrl();
            mergeState({termValue: urlTerm, term: urlTerm});
            let config = (await axios.get(Config.oauth + '/integration/getconfig')).data;
            let testCaseGroups = {};
            if(!Config.production){
                testCaseGroups = (await axios.get(Config.api + '/odata/Company/Functions.GetTestCaseGroups')).data;
            }
            mergeState({tags: config.Tags || [], customFields: config.CustomFields, loaded: true, testCaseGroups});
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const deleteIntegration = async (id: string | number, confirm?: boolean) => {
        if(confirm){
            try{
                await axios.post(Config.oauth + '/integration/delete/' + id);
                mergeState(prev => ({showConfirm: false, refresh: !prev.refresh}));
            }catch(e: any){
                let error = 'Unexpected error.';
                if(e.response && e.response.data && e.response.data.error){
                    error = e.response.data.error;
                }
                mergeState({showConfirm: false, showError: true, error});
            }
        } else {
            mergeState({showConfirm: true, confirmMessage: 'Are you sure you want to remove this integration? If it has already been deployed to stage it will be moved to "Ignored" priority instead.', onConfirm: () => deleteIntegration(id, true)});
        }
    };
    const newIntegration = async (e: any) => {
        try{
            await axios.post(Config.oauth + '/integration/create', e);
            mergeState(prev => ({newIntegration: false, refresh: !prev.refresh}));
        }catch(err){
            let error = 'Unexpected error.';
            if(err.response && err.response.data && err.response.data.error){
                error = err.response.data.error;
            }
            mergeState({showError: true, error});
        }
    };
    const exportSelected = () => {
        let header = ['Company Name', 'Primary Contact', 'Tags', 'Type', 'Status', 'Error', 'Priority', 'Test Group', 'Days Onboarding', 'Communication Attempts', 'Started', 'Requested Go Live', 'Live'];
        let customFields = state.customFields || [];
        customFields.forEach((r: any) => header.push(r.Name));
        let rows = [header].concat(state.selected.map((r: any) => convertRow(r, customFields)));
        let output = Papa.unparse(rows);
        let blob = new Blob([output], { type: "text/csv" });
        FileSaver.saveAs(blob, 'onboarding.csv');
    };
    const convertRow = (r: any, customFields: any[]) => {
        let row: any[] = [r.CompanyName, r.Email, (r.Tags || "").split("|").filter((r: string) => !(!r)).join(","),
        r.IntegrationType, r.Status, r.ErrorMessage, r.Priority, r.TestGroup, r.Days, r.ReminderCount,
        formatExportDate(r.StartDate), formatExportDate(r.RequestedLiveDate), formatExportDate(r.GoLiveDate)];
        customFields.forEach(field => {
            row.push(r[field.Id] || "");
        });
        return row;
    };
    const formatExportDate = (date: any) => {
        if(!date){
            return '';
        }
        return momentjs(date).utc().format('YYYY-MM-DD')
    };
    const downloadTemplate = () => {
        let rows = Config.production ? [['Company', 'Email', 'Priority', 'Note']] : [['Company', 'Email', 'Priority', 'RequestedGoLive', 'Note']];
        if((state.testCaseGroups || {}).Enabled){
            rows[0].push("TestGroup");
        }
        let output = Papa.unparse(rows);
        let blob = new Blob([output], { type: "text/csv" });
        FileSaver.saveAs(blob, 'bulk_onboard_template.csv');
    };
    const onDrop = (files: File[]) => {
        if(files && files.length > 0){
            mergeState({importFile: files[0]});
        }
    };
    const bulkInvite = async () => {
        try{
            let testGroupsEnabled = (state.testCaseGroups || {}).Enabled;
            let input = await readFileAsync(state.importFile);
            let records: any[] = Papa.parse(input, {header: true, skipEmptyLines: true}).data;
            let data = records.map(r => ({CompanyName: r.Company, Email: r.Email, Priority: r.Priority, RequestedLiveDate: r.RequestedGoLive, Note: r.Note, TestGroup: testGroupsEnabled ? r.TestGroup : null}));
            if(testGroupsEnabled){
                let testGroups = data.map(r => r.TestGroup).filter(r => !(!r));
                testGroups = testGroups.filter((r, i) => testGroups.findIndex(x => x === r) === i);
                let invalidTestGroups = testGroups.filter(r => ((state.testCaseGroups || {}).Groups || []).findIndex((x: any) => x === r) < 0);
                if(invalidTestGroups.length > 0){
                    mergeState({showError: true, error: "These test group(s) do not exist: " + invalidTestGroups.join(", ")});
                    return;
                }
            }
            let res = (await axios.post(Config.oauth + '/integration/createmany', data)).data;
            mergeState(prev => ({refresh: !prev.refresh, created: res}));
        }catch(err: any){
            let error = 'Unexpected error.';
            if(err.response && err.response.data && err.response.data.error){
                error = err.response.data.error;
            }
            mergeState({showError: true, error});
        }
    };
    const onEnter = async (e: React.SyntheticEvent<HTMLInputElement>) => {
        var term = e.currentTarget.value;
        mergeState({term});
        new Helper().setTermInUrl(term, props.history, props.location);
    };
    const bulkGoLive = async (status: string) => await bulkAction(status, Config.oauth + '/integration/golive/');
    const bulkAccept = async (status: string) => await bulkAction(status, Config.oauth + '/integration/operatoraccept/');
    const bulkResendInvite = async (status: string) => await bulkAction(status, Config.oauth + '/integration/ResendInitialEmail/');
    const bulkResendUserInvite = async (status: string) => await bulkAction(status, Config.oauth + '/integration/resendinvite/');
    const bulkAction = async (status: string, url: string) => {
        let integrations = state.selected.filter(r => r.Status === status);
        let results: any[] = [];
        mergeState({bulkProgress: 0.1, bulkResults: []});
        for(let i = 0; i < integrations.length; i++){
            let integration = integrations[i];
            try{
                await axios.post(url + integration.Id);
            }catch(e: any){
                let error = 'Unexpected error.';
                if(e.response && e.response.data && e.response.data.error){
                    error = e.response.data.error;
                }
                results.push({...integration, Error: error});
            }finally{
                mergeState({bulkProgress: ((i + 1) / integrations.length) * 100});
            }
        }
        if(results.length > 0){
            mergeState(prev => ({bulkResults: results, refresh: !prev.refresh}));
        } else {
            mergeState(prev => ({refresh: !prev.refresh, showBulkActions: false}));
        }
    };
    const handleShowBulkActions = () => mergeState({showBulkActions: true, bulkResults: [], bulkProgress: 0});
        let bulkResultConfig = {
            getData: () => state.bulkResults,
            columns: [
                { id: "Id", hidden: true},
                { id: 'CompanyName', filterable: true, sortable: true, label: 'Company Name', width: '25em' },
                { id: "Error", filterable: true, label: 'Error'}
            ],
            keyField: 'Id',
            order: 'asc',
            orderBy: 'CompanyName',
            pageSize: 10,
            pageSizes: [10, 25, 50, 100],
            title: 'Errors'
        };
        let columns: any[] = [
            { id: 'Id', hidden: true },
            { id: 'Error', hidden: true, type: 'boolean' },
            { id: 'ErrorMessage', hidden: true },
            { id: 'CompanyName', filterable: true, sortable: true, label: 'Company Name' },
            { id: 'Tags', allowStringOperators: true, stopPropagation: true, filterable: true, sortable: false, label: 'Tags', width: '15em', template: (value, row, addFilter) => <RenderTags key={row.Id} value={value} addFilter={addFilter}/> },
            { id: 'Email', hidden: true, toggleable: true, filterable: true, sortable: true, label: 'Primary contact', width: '20em' },
            { id: 'Status', filterable: true, sortable: true, label: 'Status', width: '12em', style: {textOverflow: 'clip'}, stopPropagation: true, template: (value, row, addFilter) => <><StatusChip value={value} error={row.ErrorMessage} onClick={() => addFilter({operator: 'eq', value: value, field: 'Status'})}/></> },
            { id: 'Priority', filterable: true, sortable: true, label: 'Priority', width: '10em', stopPropagation: true, template: (value, row, addFilter) => value ? <PriorityChip value={value} onClick={() => addFilter({operator: 'eq', value: value, field: 'Priority'})}/> : '' },
            { id: 'TestGroup', hidden: true, toggleable: true, label: 'Test Group' },
            { id: 'IntegrationType', hidden: true, toggleable: true, filterable: true, sortable: true, label: 'Type', width: '10em' },
            { id: 'Days', type:'number', hidden: Config.production, toggleable: true, filterable: true, sortable: true, label: 'Days Onboarding', width: '15em' },
            { id: 'StartDate', type:'date', toggleable: true, filterable: true, hidden: !Config.production, sortable: true, label: 'Started', template: (value) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>), width: '15em' },
            { id: 'RequestedLiveDate', type:'date', hidden: Config.production, toggleable: true, filterable: true, sortable: true, label: 'Requested Go Live', template: (value) => (value ? <Moment format='MM/DD/YYYY' utc>{value}</Moment> : ''), width: '12em' },
            { id: 'GoLiveDate', type:'date', hidden: !Config.production, toggleable: true, filterable: true, sortable: true, label: 'Live', template: (value) => (value ? <Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment> : ''), width: '15em' },
            { id: 'ReminderCount', type:'number', hidden: true, toggleable: true, filterable: true, sortable: true, label: 'Communication Attempts', width: '15em' },
        ];
        if(state.customFields){
            state.customFields.forEach(field => {
                columns.push({ id: field.Id, label: field.Name, hidden: true, toggleable: true, filterable: true, sortable: true, style: {textOverflow: 'clip'}});
            });
        }
        columns.push({ id: 'Id', width: '16em', stopPropagation: 'true', template: (value, row) => {return (<>
            <Button variant='outlined' component={Link} to={`/onboarding/integration?id=${row.Id}`} style={{marginRight: '1em'}}>View</Button>
            <Button variant='outlined' onClick={() => deleteIntegration(row.Id)}>Remove</Button>
        </>);}});
        let config = {
            url: Config.oauth + "/api/odata/integrations",
            columns: columns,
          term: state.term,
          order: 'asc',
          orderBy: 'CompanyName',
          keyField: 'Id',
          selectable: true,
          pageSize: 20,
          pageSizes: [10, 20, 50, 100],
          filter: state.showIgnored ? {logic: 'and', filters: [], system: true} : {logic: 'and', filters: [{operator: 'ne', field: 'Priority', value: 'Ignored', system: true}]},
          filterInUrl: true,
          title: 'Onboardings',
          refresh: state.refresh,
          actions: <div style={{marginTop: 'auto', marginBottom: 'auto', display: 'flex', flex: '1', gap: '1em'}}>
            <Button size='small' variant='outlined' component={Link} to={'/onboarding/settings'}>Settings</Button>
            <Button size='small' variant='outlined' onClick={() => {mergeState(prev => ({showIgnored: !prev.showIgnored}))}}>{!state.showIgnored ? 'Show Ignored' : 'Hide Ignored'}</Button>
            <Button size='small' onClick={() => mergeState({newIntegration: true})} color="primary" variant='contained' style={{minWidth: '10em'}}>Invite Supplier</Button>
            <div>
                <SearchBox placeholder="Search" onEnter={onEnter} handleChange={(e) => mergeState({termValue: e.currentTarget.value})} term={state.termValue}/>
            </div>
            </div>,
          selectedActions: <div style={{display: 'flex'}}>
                <Button onClick={handleShowBulkActions}>Bulk Actions</Button>
              <Button onClick={exportSelected}>Export</Button>
            </div>,
          setSelected: (ids, models) => {
            // Return the SAME state reference when unchanged so React bails out —
            // EnhancedTable.componentDidUpdate calls this on every update, and routing
            // an equal case through mergeState (which always spreads a new object)
            // would re-render endlessly. Mirrors onboarding_v2's handleSetSelected.
            setState(prev => isEqual(models, prev.selected) ? prev : {...prev, selected: models});
          }
        }
        if(!state.loaded){
            return <div/>;
        }
        return (<div>
                    <EnhancedTable config={config}/>
                    <Dialog open={state.showError} onClose={() => {mergeState({showError: false})}}>
                        <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
                        <DialogContent><Typography>{state.error}</Typography></DialogContent>
                    </Dialog>
                    <Dialog open={state.newIntegration} onClose={() => {mergeState({newIntegration: false})}} fullWidth maxWidth="sm">
                        <Toolbar className='lbtoolbar'>Invite Supplier</Toolbar>
                        <Tabs value={state.selectedTab} onChange={(e, newValue) => mergeState({selectedTab: newValue})} style={{paddingLeft: '24px', paddingRight: '24px'}}>
                            <Tab label='Single Invite' value='single'></Tab>
                            <Tab label='Bulk Invite' value='bulk'></Tab>
                        </Tabs>
                        {state.selectedTab === 'single' &&
                            <Form onSubmit={newIntegration}
                            render={({ handleSubmit, pristine, invalid, values, form }) => (
                            <form autoComplete='off' onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', flex: 1}}>
                            <DialogContent>
                            <div style={{display: 'flex', flex: '1 1 auto', position: 'relative'}}>
                                <Field style={{width: '100%'}} validate={v => v ? undefined : 'Company name is required.'} label='Company name *' component={ExtendedField} name='CompanyName'/>
                            </div>
                            <div style={{display: 'flex', flex: '1 1 auto', position: 'relative'}}>
                                <Field style={{width: '100%'}} validate={v => v ? validateEmail(v) : 'Email address is required.'} label='Primary contact email *' type='email' component={ExtendedField} name='Email'/>
                            </div>
                            {(state.testCaseGroups || {}).Enabled && <div style={{display: 'flex', flex: '1 1 auto', position: 'relative'}}>
                                <Field component={SelectField as any} style={{paddingBottom: '0.5em'}} formControlProps={{style: {width: '100%'}}} shrink label='Test group' name='TestGroup'>
                                    <MenuItem value="">None</MenuItem>
                                    {state.testCaseGroups.Groups.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                </Field>
                            </div>}
                            <div style={{display: 'flex', flex: '1 1 auto', position: 'relative'}}>
                                <Field component={SelectField as any} style={{paddingBottom: '0.5em'}} formControlProps={{style: {width: '100%'}}} shrink name="Priority" label='Priority'>
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="Ignored"><PriorityChip size="small" value="Ignored"/></MenuItem>
                                    <MenuItem value="Low"><PriorityChip size="small" value="Low"/></MenuItem>
                                    <MenuItem value="Medium"><PriorityChip size="small" value="Medium"/></MenuItem>
                                    <MenuItem value="High"><PriorityChip size="small" value="High"/></MenuItem>
                                </Field>
                            </div>
                            {!Config.production && <div style={{display: 'flex', flex: '1 1 auto', position: 'relative'}}>
                                <Field style={{width: '100%'}} label='Requested live date' type='date' component={ExtendedField} name='RequestedLiveDate'/>
                            </div>}
                            <div style={{display: 'flex', flex: '1 1 auto', position: 'relative'}}>
                                <Field style={{width: '100%'}} label='Note' component={ExtendedField} name='Note'/>
                            </div>
                            </DialogContent>
                            <DialogActions>
                                <DownloadButton color="primary" onClick={handleSubmit} type='submit' disabled={pristine || invalid}>Send Invite</DownloadButton>
                            </DialogActions>
                            </form>
                        )}/>}
                        {state.selectedTab === 'bulk' &&
                            <>
                            {!state.created && <DialogContent style={{overflow: 'hidden'}}>
                                <Typography><MuiLink style={{lineHeight: 'inherit', fontSize: 'inherit', fontFamily: 'inherit', verticalAlign: 'top'}} underline="hover" component='button' onClick={downloadTemplate}>Download template</MuiLink> to bulk upload up to 50 suppliers.</Typography>
                                <Table size='small'>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{width: '6em'}}>Field</TableCell>
                                            <TableCell style={{width: '5em'}}>Required</TableCell>
                                            <TableCell>Description</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                                <TableCell>Company</TableCell>
                                                <TableCell><CheckBox /></TableCell>
                                                <TableCell>Company name</TableCell>
                                        </TableRow>
                                        <TableRow>
                                                <TableCell>Email</TableCell>
                                                <TableCell><CheckBox /></TableCell>
                                                <TableCell>Primary contact email</TableCell>
                                        </TableRow>
                                        <TableRow>
                                                <TableCell>Priority</TableCell>
                                                <TableCell></TableCell>
                                                <TableCell>Low, Medium, or High</TableCell>
                                        </TableRow>
                                        {!Config.production && <TableRow>
                                                <TableCell>RequestedGoLive</TableCell>
                                                <TableCell></TableCell>
                                                <TableCell>Format MM/DD/YYYY</TableCell>
                                        </TableRow>}
                                        <TableRow>
                                                <TableCell>Note</TableCell>
                                                <TableCell></TableCell>
                                                <TableCell>Additional message in the initial email</TableCell>
                                        </TableRow>
                                        {!(!((state.testCaseGroups || {}).Enabled)) && <TableRow>
                                                <TableCell>TestGroup</TableCell>
                                                <TableCell></TableCell>
                                                <TableCell>Supplier test group</TableCell>
                                        </TableRow>}
                                    </TableBody>
                                </Table>
                        <DropZone accept=".csv" style={{width: '100%', height: '10em'}} onDrop={onDrop}>
                        {({ isDragAccept, isDragReject, acceptedFiles, rejectedFiles }) => {
                            if (isDragAccept || isDragReject) {
                              return "Drop here to upload this file.";
                            }
                            return state.importFile ? state.importFile.name : 'Drop a CSV file here or click to choose a file.';
                          }}
                        </DropZone>
                        </DialogContent>}
                        {state.created && <DialogContent style={{padding: 0}}><Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{width: '12em'}}>Company</TableCell>
                                    <TableCell style={{width: '12em'}}>Primary contact</TableCell>
                                    <TableCell style={{width: '10em'}}>Result</TableCell>
                                    <TableCell>Message</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            {state.created.map((r, index) => <TableRow key={index}>
                                        <TableCell>{r.CompanyName}</TableCell>
                                        <TableCell>{r.Email}</TableCell>
                                        <TableCell><CreatedChip value={!r.Error}/></TableCell>
                                        <TableCell>{r.Error}</TableCell>
                                    </TableRow>)}
                            </TableBody>
                        </Table></DialogContent>}
                        {!state.created && <DialogActions>
                        <DownloadButton color="primary" onClick={bulkInvite} type='submit' disabled={!state.importFile || !state.importFile.name}>Send Invites</DownloadButton>
                        </DialogActions>}
                            </>
                        }
                    </Dialog>
                    <Dialog maxWidth={state.bulkResults.length > 0 ? 'md' : 'xs'} fullWidth open={state.showBulkActions} onClose={() => {mergeState({showBulkActions: false})}}>
                        {state.bulkResults.length === 0 && state.bulkProgress === 0 && <><Toolbar className='lbtoolbar'>Bulk Onboarding Actions</Toolbar>
                        <List style={{padding: 0}}>
                            <ListItem button disabled={!state.selected.filter(r => r.Status === OnboardingStatus.InviteSent).length} onClick={() => bulkResendInvite(OnboardingStatus.InviteSent)}>
                                <ListItemText primary="Resend onboarding invite" secondary={`${state.selected.filter(r => r.Status === OnboardingStatus.InviteSent).length} selected companies in ${OnboardingStatus.InviteSent} status`} />
                                <ArrowForwardIosIcon color="action"/>
                            </ListItem>
                            <ListItem button disabled={!state.selected.filter(r => r.Status === OnboardingStatus.FormReceived).length} onClick={() => bulkAccept(OnboardingStatus.FormReceived)}>
                                <ListItemText primary="Accept onboarding form" secondary={`${state.selected.filter(r => r.Status === OnboardingStatus.FormReceived).length} selected companies in ${OnboardingStatus.FormReceived} status`} />
                                <ArrowForwardIosIcon color="action"/>
                            </ListItem>
                            <ListItem button disabled={!state.selected.filter(r => r.Status === OnboardingStatus.Onboarding).length} onClick={() => bulkResendUserInvite(OnboardingStatus.Onboarding)}>
                                <ListItemText primary="Resend user invite" secondary={`${state.selected.filter(r => r.Status === OnboardingStatus.Onboarding).length} selected companies in ${OnboardingStatus.Onboarding} status`} />
                                <ArrowForwardIosIcon color="action"/>
                            </ListItem>
                            <ListItem button disabled={!state.selected.filter(r => r.Status === OnboardingStatus.Onboarding).length} onClick={() => bulkGoLive(OnboardingStatus.Onboarding)}>
                                <ListItemText primary="Push supplier live" secondary={`${state.selected.filter(r => r.Status === OnboardingStatus.Onboarding).length} selected companies in ${OnboardingStatus.Onboarding} status`} />
                                <ArrowForwardIosIcon color="action"/>
                            </ListItem>
                            <ListItem button disabled={!state.selected.filter(r => r.Status === OnboardingStatus.RequestedGoLive).length} onClick={() => bulkGoLive(OnboardingStatus.RequestedGoLive)}>
                                <ListItemText primary="Approve to go live" secondary={`${state.selected.filter(r => r.Status === OnboardingStatus.RequestedGoLive).length} selected companies in ${OnboardingStatus.RequestedGoLive} status`} />
                                <ArrowForwardIosIcon color="action"/>
                            </ListItem>
                        </List></>}
                        {state.bulkResults.length === 0 && state.bulkProgress > 0 && <>
                        <Toolbar className='lbtoolbar'>Processing Bulk Action</Toolbar>
                        <DialogContent>
                            <div style={{display: 'flex', flex: 1, placeItems: 'center', justifyContent: 'center'}}>
                                <LinearProgress style={{height: '50px', width: '150px'}} variant='determinate' value={state.bulkProgress}/>
                            </div>
                        </DialogContent>
                        </>}
                        {state.bulkResults.length > 0 ? <EnhancedTable panelStyle={{boxShadow: 'none'}} wrapperStyle={{maxHeight: '400px'}} config={bulkResultConfig}/> : <span/>}
                    </Dialog>
                    <ConfirmDialog open={state.showConfirm} onClose={() => mergeState({showConfirm: false})} message={state.confirmMessage} onConfirm={state.onConfirm}/>
                    <input type="file" accept='.pdf' id="agreementUpload" ref={(ref) => { agreementUpload.current = ref; }} style={{display: "none"}} onChange={(e) => saveAgreement(e.target.files)}/>
                </div>);
};

export default withRouter(Onboarding as any);
export {
    PriorityChip,
    StatusChip,
    CreatedChip,
    RenderTags,
    OnboardingStatus
};
